import React, { useMemo, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileUp, Download, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import StatCard from "@/components/StatCard";

const TEMPLATE_HEADERS = [
  "date",
  "vehicle",
  "is_halt",
  "sales",
  "deposit",
  "net_earnings",
  "extra_income",
  "extra_income_remark",
  "expense_amount",
  "expense_remark",
  "savings_credit",
  "savings_debit",
  "savings_remark",
  "remark",
];

const SAMPLE_CSV = `date,vehicle,is_halt,sales,deposit,net_earnings,extra_income,extra_income_remark,expense_amount,expense_remark,savings_credit,savings_debit,savings_remark,remark
2026-04-01,Bus 1,N,5400,370,2800,200,parcel,450,diesel,500,0,daily save,
2026-04-02,Bus 1,N,5100,370,2600,0,,420,diesel,500,0,,
2026-04-03,Bus 1,Y,0,0,0,0,,0,,500,0,halt savings,maintenance day
2026-04-01,Bus 2,N,4800,370,2400,0,,400,diesel,500,0,,`;

const splitCsvLine = (line) => {
  const out = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQ && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQ = !inQ;
      }
    } else if (c === "," && !inQ) {
      out.push(cur);
      cur = "";
    } else {
      cur += c;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
};

const parseCsv = (text) => {
  const lines = text
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length < 2) return { rows: [], errors: ["No data rows found"] };
  const header = splitCsvLine(lines[0]).map((h) => h.toLowerCase().trim());
  const idx = (k) => header.indexOf(k);
  const required = ["date", "vehicle"];
  const missing = required.filter((r) => idx(r) === -1);
  if (missing.length) {
    return { rows: [], errors: [`Missing required columns: ${missing.join(", ")}`] };
  }
  const rows = [];
  const errors = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = splitCsvLine(lines[i]);
    const get = (k) => {
      const j = idx(k);
      return j === -1 ? "" : cells[j] || "";
    };
    const num = (k) => {
      const v = get(k);
      if (v === "") return 0;
      const n = Number(v);
      if (Number.isNaN(n)) return 0;
      return n;
    };
    const haltVal = (get("is_halt") || "").toLowerCase();
    const isHalt = ["y", "yes", "true", "1"].includes(haltVal);
    rows.push({
      date: get("date"),
      vehicle: get("vehicle"),
      is_halt: isHalt,
      sales: num("sales"),
      deposit: num("deposit"),
      net_earnings: num("net_earnings"),
      extra_income: num("extra_income"),
      extra_income_remark: get("extra_income_remark"),
      expense_amount: num("expense_amount"),
      expense_remark: get("expense_remark"),
      savings_credit: num("savings_credit"),
      savings_debit: num("savings_debit"),
      savings_remark: get("savings_remark"),
      remark: get("remark"),
    });
  }
  return { rows, errors };
};

export default function BulkImport() {
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const parsed = useMemo(() => (text ? parseCsv(text) : { rows: [], errors: [] }), [text]);

  const downloadTemplate = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "vivan_paribahan_import_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const t = await f.text();
    setText(t);
    setResult(null);
  };

  const onImport = async () => {
    if (!parsed.rows.length) {
      toast.error("Nothing to import — paste your CSV first");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/import/entries", { rows: parsed.rows });
      setResult(res.data);
      const { inserted, updated, errors } = res.data;
      if (errors.length === 0) {
        toast.success(`Imported ${inserted} new + ${updated} updated`);
      } else {
        toast.warning(
          `Imported ${inserted + updated}, ${errors.length} skipped (see details)`
        );
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="bulk-import-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="label-eyebrow">Migration</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-1">
            Bulk Import
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Paste or upload a CSV to load months of historical entries in one go.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={downloadTemplate}
          className="gap-2"
          data-testid="download-template-btn"
        >
          <Download className="h-4 w-4" /> Download Template
        </Button>
      </div>

      <div className="card-swiss p-5">
        <div className="label-eyebrow mb-2">Step 1 · CSV Format</div>
        <p className="text-sm text-gray-600 leading-relaxed">
          Required columns:{" "}
          <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-[12px]">
            date
          </code>
          ,{" "}
          <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-[12px]">
            vehicle
          </code>
          . Vehicle can be either the bus name (e.g.,{" "}
          <span className="font-mono">Bus 1</span>) or registration number (e.g.,{" "}
          <span className="font-mono">WB05C7216</span>). Date must be{" "}
          <span className="font-mono">YYYY-MM-DD</span>. Halt days use{" "}
          <span className="font-mono">Y / N</span>. All numeric columns default to 0
          if blank. Same vehicle + date will <strong>update</strong> existing
          entries, not duplicate.
        </p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {TEMPLATE_HEADERS.map((h) => (
            <span
              key={h}
              className="text-[11px] px-2 py-0.5 border border-gray-200 rounded font-mono text-gray-700"
            >
              {h}
            </span>
          ))}
        </div>
      </div>

      <div className="card-swiss p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="label-eyebrow">Step 2 · Paste or Upload CSV</div>
          </div>
          <label className="cursor-pointer">
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={onFile}
              data-testid="csv-file-input"
            />
            <span className="inline-flex items-center gap-2 text-sm border border-gray-200 rounded-md px-3 py-1.5 hover:bg-gray-50">
              <FileUp className="h-4 w-4" /> Upload .csv
            </span>
          </label>
        </div>
        <Textarea
          rows={10}
          placeholder={SAMPLE_CSV}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setResult(null);
          }}
          className="font-mono text-xs"
          data-testid="csv-textarea"
        />
        {parsed.errors.length > 0 && (
          <div className="mt-3 p-3 border border-red-200 bg-red-50 rounded-md text-sm text-red-700">
            {parsed.errors.map((e, i) => (
              <div key={i}>{e}</div>
            ))}
          </div>
        )}
      </div>

      {parsed.rows.length > 0 && (
        <div className="card-swiss p-5" data-testid="preview-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-eyebrow">Step 3 · Preview ({parsed.rows.length} rows)</div>
            </div>
            <Button
              onClick={onImport}
              disabled={submitting}
              className="gap-2"
              data-testid="import-btn"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileUp className="h-4 w-4" />
              )}
              Import {parsed.rows.length} rows
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-200 text-left uppercase tracking-wider text-gray-500">
                  <th className="py-2 pr-3 font-semibold">Date</th>
                  <th className="py-2 px-2 font-semibold">Vehicle</th>
                  <th className="py-2 px-2 font-semibold">Halt</th>
                  <th className="py-2 px-2 text-right font-semibold">Sales</th>
                  <th className="py-2 px-2 text-right font-semibold">Deposit</th>
                  <th className="py-2 px-2 text-right font-semibold">Earnings</th>
                  <th className="py-2 px-2 text-right font-semibold">Extra</th>
                  <th className="py-2 px-2 text-right font-semibold">Expense</th>
                  <th className="py-2 px-2 text-right font-semibold">Save+</th>
                  <th className="py-2 px-2 text-right font-semibold">Save−</th>
                </tr>
              </thead>
              <tbody>
                {parsed.rows.slice(0, 50).map((r, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="py-1.5 pr-3 font-mono">{r.date}</td>
                    <td className="py-1.5 px-2">{r.vehicle}</td>
                    <td className="py-1.5 px-2">{r.is_halt ? "Y" : "N"}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{r.sales}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{r.deposit}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{r.net_earnings}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{r.extra_income}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{r.expense_amount}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{r.savings_credit}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{r.savings_debit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {parsed.rows.length > 50 && (
              <p className="text-xs text-gray-500 mt-3">
                Showing first 50 of {parsed.rows.length} rows. All will be imported.
              </p>
            )}
          </div>
        </div>
      )}

      {result && (
        <div className="card-swiss p-5" data-testid="result-card">
          <div className="label-eyebrow mb-3">Result</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <StatCard
              label="New Entries"
              value={result.inserted}
              accent="text-emerald-700"
              testid="result-inserted"
            />
            <StatCard
              label="Updated"
              value={result.updated}
              accent="text-blue-700"
              testid="result-updated"
            />
            <StatCard
              label="Errors"
              value={result.errors.length}
              accent={result.errors.length > 0 ? "text-red-600" : "text-gray-500"}
              testid="result-errors"
            />
          </div>
          {result.errors.length > 0 ? (
            <div className="border border-red-200 bg-red-50 rounded-md p-3">
              <div className="flex items-center gap-2 text-red-700 text-sm font-semibold mb-2">
                <AlertCircle className="h-4 w-4" /> Skipped rows
              </div>
              <ul className="text-xs text-red-700 space-y-1 max-h-[200px] overflow-y-auto">
                {result.errors.map((e, i) => (
                  <li key={i} className="font-mono">
                    Row {e.row}: {e.error}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-emerald-700 text-sm">
              <CheckCircle2 className="h-4 w-4" /> All rows imported successfully.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
